import { useState } from "react";
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Alert,
  Chip,
  Paper,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import MapIcon from "@mui/icons-material/Map";

type Floor = {
  level: string;
  height: string;
  area: string;
  heating: string;
  workTypes: string[];
};

const floorLevels = [
  "KG","EG","1.OG","2.OG","3.OG","4.OG",
  "5.OG","6.OG","7.OG","8.OG","9.OG","10.OG",
];

const workOptions = [
  "Zementestrich",
  "Anhydritestrich",
  "Dämmung auslegen",
  "Abbrucharbeiten",
];

export default function ContactForm() {
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [mapAccepted, setMapAccepted] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [floors, setFloors] = useState<Floor[]>([
    { level: "", height: "", area: "", heating: "", workTypes: [] },
  ]);

  const [form, setForm] = useState({
    firstname: "",
    lastname: "",
    street: "",
    zip: "",
    city: "",
    email: "",
    phone: "",
    siteAddress: "",
    date: "",
    notes: "",
  });

  const isNumber = (value: string) => /^\d+$/.test(value);
  const isEmail = (value: string) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const requiredError = (value: string) => submitted && value.trim() === "";

  const handleChange =
    (field: string) => (e: React.ChangeEvent<HTMLInputElement>) =>
      setForm({ ...form, [field]: e.target.value });

  const handleFloorChange = (
    index: number,
    field: keyof Floor,
    value: any
  ) => {
    const updated = [...floors];
    updated[index][field] = value;
    setFloors(updated);
  };

  const addFloor = () =>
    setFloors([
      ...floors,
      { level: "", height: "", area: "", heating: "", workTypes: [] },
    ]);

  const removeFloor = (index: number) =>
    setFloors(floors.filter((_, i) => i !== index));

  const floorHasError = (f: Floor) =>
    submitted &&
    (!f.level ||
      !f.area ||
      !f.heating ||
      f.workTypes.length === 0 ||
      (f.area && !isNumber(f.area)) ||
      (f.height && !isNumber(f.height)));

  const handleSubmit = () => {
    setSubmitted(true);

    if (
      !form.firstname ||
      !form.lastname ||
      !form.street ||
      !form.zip ||
      !form.city ||
      !form.email ||
      !privacyAccepted ||
      floors.some(floorHasError) ||
      !isNumber(form.zip) ||
      !isEmail(form.email)
    ) return;

    const floorText = floors
      .map(
        (f, i) =>
          `Stockwerk ${i + 1}: ${f.level}\nFläche: ${f.area} m²\nAufbauhöhe: ${f.height || "-"} mm\nFußbodenheizung: ${f.heating}\nLeistungen: ${f.workTypes.join(", ")}`
      )
      .join("\n\n");

    const body = `
Sehr geehrtes EDEB-Team, anbei senden wir Ihnen folgende Anfrage:

PERSON
Vorname: ${form.firstname}
Nachname: ${form.lastname}
Straße: ${form.street}
PLZ / Ort: ${form.zip} ${form.city}
E-Mail: ${form.email}
Telefon: ${form.phone}

BAUVORHABEN
Baustellenadresse: ${form.siteAddress}

${floorText}

Ausführungsdatum: ${form.date}

Weitere Anmerkungen:
${form.notes}
`;

    window.location.href = `mailto:edeb-estrichbau@web.de?subject=Anfrage&body=${encodeURIComponent(
      body
    )}`;
  };

  return (
    <Box sx={{ flex: 1, py: 8 }}>
      <Container id="contact" maxWidth="lg">
        <Typography variant="h3" color="primary" gutterBottom sx={{ textAlign: "center", mb: 6 }}>
          Kontakt & Angebotsanfrage
        </Typography>

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 6 }}>
          
          {/* Left Column: Contact Details & Google Maps */}
          <Box>
            <Paper elevation={0} sx={{ p: 4, mb: 4, backgroundColor: "background.paper", borderRadius: 2 }}>
              <Typography variant="h5" color="primary" gutterBottom>
                Unser Standort
              </Typography>
              <Typography variant="body1" sx={{ color: "text.secondary", mb: 1 }}>
                EDEB-Estrichbau
              </Typography>
              <Typography variant="body1" sx={{ color: "text.secondary", mb: 1 }}>
                65760 Eschborn
              </Typography>
              <Typography variant="body1" sx={{ color: "text.secondary", mb: 1 }}>
                Telefon: 0162 4196199
              </Typography>
              <Typography variant="body1" sx={{ color: "text.secondary", mb: 3 }}>
                E-Mail: edeb-estrichbau@web.de
              </Typography>

              {/* Google Maps GDPR Conform */}
              <Box sx={{ height: 300, borderRadius: 2, overflow: "hidden", border: "1px solid", borderColor: "divider" }}>
                {!mapAccepted ? (
                  <Box sx={{ height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", backgroundColor: "background.default", p: 3, textAlign: "center" }}>
                    <MapIcon sx={{ fontSize: 48, color: "text.secondary", mb: 2 }} />
                    <Typography variant="h6" gutterBottom color="text.primary">Google Maps laden</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      Mit dem Klick auf "Karte laden" stimmen Sie zu, dass Ihre IP-Adresse an Google übertragen wird.
                    </Typography>
                    <Button variant="contained" color="primary" onClick={() => setMapAccepted(true)}>
                      Karte laden
                    </Button>
                  </Box>
                ) : (
                  <iframe
                    title="Google Maps"
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2558.118933227909!2d8.566667!3d50.141667!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2sEschborn!5e0!3m2!1sde!2sde!4v1600000000000!5m2!1sde!2sde"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen={true}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  ></iframe>
                )}
              </Box>
            </Paper>
          </Box>

          {/* Right Column: Form */}
          <Box>
            <Paper elevation={0} sx={{ p: { xs: 3, md: 5 }, backgroundColor: "background.paper", borderRadius: 2 }}>
              <Typography variant="h5" color="primary" gutterBottom>
                Direktanfrage
              </Typography>
              
              {submitted && !privacyAccepted && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  Bitte akzeptieren Sie die Datenschutzerklärung.
                </Alert>
              )}

              <Typography variant="h6" sx={{ mt: 2, mb: 2 }}>
                Person
              </Typography>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <TextField label="Vorname *" value={form.firstname} onChange={handleChange("firstname")} error={!!requiredError(form.firstname)} helperText={requiredError(form.firstname) ? "Pflichtfeld" : ""} />
                <TextField label="Nachname *" value={form.lastname} onChange={handleChange("lastname")} error={!!requiredError(form.lastname)} helperText={requiredError(form.lastname) ? "Pflichtfeld" : ""} />
                <TextField label="Straße *" value={form.street} onChange={handleChange("street")} error={!!requiredError(form.street)} helperText={requiredError(form.street) ? "Pflichtfeld" : ""} />
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                  <TextField label="PLZ *" value={form.zip} onChange={handleChange("zip")} error={!!(requiredError(form.zip) || (submitted && !isNumber(form.zip)))} helperText={requiredError(form.zip) ? "Pflichtfeld" : submitted && !isNumber(form.zip) ? "Nur Zahlen erlaubt" : ""} sx={{ flex: 1, minWidth: 140 }} />
                  <TextField label="Ort *" value={form.city} onChange={handleChange("city")} error={!!requiredError(form.city)} helperText={requiredError(form.city) ? "Pflichtfeld" : ""} sx={{ flex: 2, minWidth: 140 }} />
                </Box>
                <TextField label="E-Mail *" value={form.email} onChange={handleChange("email")} error={!!(requiredError(form.email) || (submitted && !isEmail(form.email)))} helperText={requiredError(form.email) ? "Pflichtfeld" : submitted && !isEmail(form.email) ? "Ungültige E-Mail" : ""} />
                <TextField label="Telefon (optional)" value={form.phone} onChange={handleChange("phone")} />
              </Box>

              <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
                Infos zum Bauvorhaben
              </Typography>

              <TextField label="Adresse der Baustelle *" fullWidth value={form.siteAddress} onChange={handleChange("siteAddress")} error={!!requiredError(form.siteAddress)} helperText={requiredError(form.siteAddress) ? "Pflichtfeld" : ""} />

              {floors.map((floor, i) => (
                <Box key={i} sx={{ mt: 3, p: 2, border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
                  <Typography variant="subtitle2" sx={{ mb: 2, color: "primary.main" }}>Stockwerk {i + 1}</Typography>
                  <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                    <TextField select label="Stockwerk *" value={floor.level} onChange={(e) => handleFloorChange(i, "level", e.target.value)} error={!!(floorHasError(floor) && !floor.level)} helperText={floorHasError(floor) && !floor.level ? "Pflichtfeld" : ""} sx={{ minWidth: 140, flex: 1 }}>
                      {floorLevels.map((l) => <MenuItem key={l} value={l}>{l}</MenuItem>)}
                    </TextField>

                    <TextField label="Aufbauhöhe (mm)" value={floor.height} onChange={(e) => handleFloorChange(i, "height", e.target.value)} error={!!(submitted && floor.height !== "" && !isNumber(floor.height))} helperText={submitted && floor.height !== "" && !isNumber(floor.height) ? "Nur Zahlen erlaubt" : ""} sx={{ minWidth: 140, flex: 1 }} />

                    <TextField label="Fläche (m²) *" value={floor.area} onChange={(e) => handleFloorChange(i, "area", e.target.value)} error={!!(floorHasError(floor) && (!floor.area || !isNumber(floor.area)))} helperText={floorHasError(floor) && !floor.area ? "Pflichtfeld" : floorHasError(floor) && floor.area && !isNumber(floor.area) ? "Nur Zahlen erlaubt" : ""} sx={{ minWidth: 140, flex: 1 }} />

                    <TextField select label="Fußbodenheizung *" value={floor.heating} onChange={(e) => handleFloorChange(i, "heating", e.target.value)} error={!!(floorHasError(floor) && !floor.heating)} helperText={floorHasError(floor) && !floor.heating ? "Pflichtfeld" : ""} sx={{ minWidth: 140, flex: 1 }}>
                      <MenuItem value="Ja">Ja</MenuItem><MenuItem value="Nein">Nein</MenuItem>
                    </TextField>

                    <TextField select label="Leistungen *" value={floor.workTypes} onChange={(e) => handleFloorChange(i, "workTypes", e.target.value)} error={!!(floorHasError(floor) && floor.workTypes.length === 0)} helperText={floorHasError(floor) && floor.workTypes.length === 0 ? "Pflichtfeld" : ""} sx={{ minWidth: 140, flex: "1 1 100%" }} SelectProps={{ multiple: true, renderValue: (selected) => (<Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>{(selected as string[]).map((v) => (<Chip key={v} label={v} size="small" />))}</Box>) }}>
                      {workOptions.map((w) => <MenuItem key={w} value={w}>{w}</MenuItem>)}
                    </TextField>

                    {floors.length > 1 && (
                      <Button color="error" startIcon={<DeleteIcon />} onClick={() => removeFloor(i)} sx={{ mt: 1 }}>Entfernen</Button>
                    )}
                  </Box>
                </Box>
              ))}

              <Button sx={{ mt: 2 }} variant="outlined" onClick={addFloor}>
                Weiteres Stockwerk hinzufügen
              </Button>

              <TextField type="date" label="Voraussichtliches Ausführungsdatum" fullWidth InputLabelProps={{ shrink: true }} sx={{ mt: 4 }} value={form.date} onChange={handleChange("date")} />

              <TextField label="Weitere Anmerkungen" multiline rows={4} fullWidth sx={{ mt: 3 }} value={form.notes} onChange={handleChange("notes")} />

              <Box sx={{ display: "flex", flexDirection: "column", gap: 3, mt: 4 }}>
                <FormControlLabel control={<Checkbox checked={privacyAccepted} onChange={(e) => setPrivacyAccepted(e.target.checked)} color="primary" />} label="Ich akzeptiere die Datenschutzerklärung *" sx={{ color: "text.primary" }} />

                <Button variant="contained" size="large" color="primary" disabled={!privacyAccepted} onClick={handleSubmit} fullWidth>
                  Anfrage absenden
                </Button>
              </Box>
            </Paper>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
